import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { retryConversation, stopConversation } from "../api.js";
function useConciergeChat(options) {
  const {
    apiBaseUrl,
    conversationId,
    sharedConversationId,
    appConfigId,
    devOptions,
    pdpStacklineSku,
    isOnChatWidget,
    onError,
    onTripwire,
    onConversationIdGenerated
  } = options;
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const sharedConversationIdRef = useRef(sharedConversationId);
  sharedConversationIdRef.current = sharedConversationId;
  const transport = useMemo(
    () => new DefaultChatTransport({
      api: `${apiBaseUrl}/concierge/agent/stream`,
      headers: async () => ({
        ...await getHeadersWithUniqueVisitorId(),
        Accept: "text/event-stream"
      }),
      body: {
        get conversationId() {
          return conversationIdRef.current;
        },
        get sharedConversationId() {
          return sharedConversationIdRef.current;
        },
        appConfigId,
        devOptions,
        ...pdpStacklineSku ? { pdpStacklineSku } : {},
        ...isOnChatWidget ? { isOnChatWidget: true } : {}
      }
    }),
    [apiBaseUrl, appConfigId, devOptions, pdpStacklineSku, isOnChatWidget]
  );
  const [transientStatus, setTransientStatus] = useState(null);
  const [tripwireMessage, setTripwireMessage] = useState(null);
  const chat = useChat({
    transport,
    onError: (error) => {
      console.error("[useConciergeChat] Stream error:", (error == null ? void 0 : error.message) || error, error);
      onError == null ? void 0 : onError(error);
    },
    onData: (dataPart) => {
      var _a, _b, _c;
      if (dataPart.type === "data-conversation-id") {
        const serverConversationId = (_a = dataPart.data) == null ? void 0 : _a.conversationId;
        if (serverConversationId && serverConversationId !== conversationIdRef.current) {
          conversationIdRef.current = serverConversationId;
          onConversationIdGenerated == null ? void 0 : onConversationIdGenerated(serverConversationId);
        }
      }
      if (dataPart.type === "data-status") {
        setTransientStatus(((_b = dataPart.data) == null ? void 0 : _b.status) ?? null);
      }
      if (dataPart.type === "data-tripwire") {
        const message = (_c = dataPart.data) == null ? void 0 : _c.message;
        if (message) {
          setTripwireMessage(message);
          onTripwire == null ? void 0 : onTripwire(message);
        }
      }
    },
    generateId: () => `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  });
  useEffect(() => {
    if (chat.status !== "streaming") {
      setTransientStatus(null);
    }
  }, [chat.status]);
  const sendMessage = useCallback(
    async (prompt, files) => {
      if (!prompt || prompt.trim().length === 0) {
        console.warn("[useConciergeChat] Ignoring empty prompt");
        return;
      }
      setTripwireMessage(null);
      await chat.sendMessage({ text: prompt, files });
    },
    [chat]
  );
  const trimLocalMessagesFromLastUser = useCallback(() => {
    chat.setMessages((prev) => {
      const lastUserIdx = prev.map((m) => m.role).lastIndexOf("user");
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });
  }, [chat]);
  const getLastUserPromptFromMessages = useCallback(() => {
    const lastUserMessage = [...chat.messages].reverse().find((m) => m.role === "user");
    if (!lastUserMessage) {
      return "";
    }
    return (lastUserMessage.parts || []).filter((p) => p.type === "text" && typeof p.text === "string").map((p) => p.text).join("").trim();
  }, [chat.messages]);
  const retryLastMessage = useCallback(async () => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      const { prompt } = await retryConversation({
        conversationId: convId,
        headersToInject: headers,
        apiBaseUrl
      });
      chat.setMessages((prev) => {
        const lastAssistantIdx = prev.map((m) => m.role).lastIndexOf("assistant");
        if (lastAssistantIdx === -1) return prev;
        const lastUserIdx = prev.slice(0, lastAssistantIdx).map((m) => m.role).lastIndexOf("user");
        const cutFrom = lastUserIdx !== -1 ? lastUserIdx : lastAssistantIdx;
        return prev.slice(0, cutFrom);
      });
      setTripwireMessage(null);
      await chat.sendMessage({ text: prompt });
    } catch (error) {
      console.error("[useConciergeChat] retryLastMessage failed:", error);
      onError == null ? void 0 : onError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [apiBaseUrl, chat, onError]);
  const cancelLatestTurn = useCallback(async () => {
    chat.stop();
    const fallbackPrompt = getLastUserPromptFromMessages();
    const convId = conversationIdRef.current;
    if (!convId) {
      trimLocalMessagesFromLastUser();
      setTripwireMessage(null);
      return fallbackPrompt || null;
    }
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      const { prompt } = await stopConversation({
        conversationId: convId,
        headersToInject: headers,
        apiBaseUrl
      });
      trimLocalMessagesFromLastUser();
      setTripwireMessage(null);
      return (prompt || fallbackPrompt || "").trim() || null;
    } catch (error) {
      console.error("[useConciergeChat] cancelLatestTurn failed:", error);
      onError == null ? void 0 : onError(error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }, [apiBaseUrl, chat, getLastUserPromptFromMessages, onError, trimLocalMessagesFromLastUser]);
  const isStreaming = chat.status === "streaming";
  const isLoading = chat.status === "submitted";
  return {
    ...chat,
    sendMessage,
    retryLastMessage,
    cancelLatestTurn,
    isStreaming,
    isLoading,
    isWaiting: isLoading || isStreaming,
    transientStatus,
    tripwireMessage
  };
}
export {
  useConciergeChat
};
//# sourceMappingURL=useConciergeChat.js.map
